import { SignIn, SignUp, Show, UserButton, UserProfile, useUser } from "@clerk/react";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { BarChart3, Bell, BookOpen, CalendarDays, ChevronRight, ClipboardCheck, FileCheck2, FileText, GraduationCap, LayoutDashboard, Menu, MessageCircle, NotebookPen, Presentation, Settings, ShieldCheck, Users, X } from "lucide-react";
import { useApi } from "./api";
import { Loading, Notice } from "./components";
import { ParticipantDashboard, ParticipantModule, ParticipantResources } from "./participant";
import { AdminAnnouncements, AdminCohorts, AdminDashboard, AdminModules, AdminParticipants, AdminRecords } from "./admin";
import { ForumHome, ForumTopicPage } from "./forum";
import { ParticipantCalendar, ParticipantCapstone, ParticipantReflections } from "./learning";
import { AdminCapstones, AdminProgramTools } from "./admin-learning";
import { ParticipantDevelopment } from "./development";
import { ParticipantRegistration } from "./registration";
import { AdminAnalytics, AdminCheckIns, AdminInstructorWorkspace } from "./admin-enhancements";
import { AdminRegistrationManagement } from "./admin-program-management";
import type { Cohort, User } from "./types";

type Session = { user: User; cohort: Cohort | null };

function AuthPages() {
  const { pathname } = useLocation();
  const accepting = pathname.startsWith("/accept-invitation");
  const signingIn = pathname.startsWith("/sign-in");
  if (!accepting && !signingIn) return <Navigate to="/sign-in" replace />;
  return <main className="auth-shell">
    <section className="auth-story">
      <div className="auth-brand"><span className="brand-mark">OL</span><span>Officer to Leader</span></div>
      <div className="auth-copy"><p className="eyebrow">Leadership development</p><h1>Lead with clarity.<br />Serve with purpose.</h1><p>A private learning environment built for structured growth, public service, and the work ahead.</p></div>
      <p className="auth-foot">Invitation-only access · Secure participant portal</p>
    </section>
    <section className="auth-form-wrap">
      <div className="auth-heading"><p className="eyebrow">Officer to Leader</p><h2>{accepting ? "Accept your invitation" : "Welcome back"}</h2><p>{accepting ? "Verify your invited email and create a password to continue." : "Sign in with your approved email and password."}</p></div>
      {accepting ? <SignUp routing="path" path="/accept-invitation" signInUrl="/sign-in" /> : <SignIn routing="path" path="/sign-in" signUpUrl="/accept-invitation" />}
      <p className="auth-help">Access is limited to invited participants. Contact your program administrator if you need help.</p>
    </section>
  </main>;
}

function AppLayout({ session }: { session: Session }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isAdmin = session.user.role === "administrator";
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { document.body.classList.toggle("portal-navigation-open", mobileOpen); return () => document.body.classList.remove("portal-navigation-open"); }, [mobileOpen]);
  const participantLinks = [
    { to: "/", label: "Overview", icon: LayoutDashboard },
    { to: "/roadmap", label: "Program roadmap", icon: BookOpen },
    { to: "/registration", label: "Session registration", icon: CalendarDays },
    { to: "/calendar", label: "Program calendar", icon: CalendarDays },
    { to: "/reflections", label: "Reflections", icon: NotebookPen },
    { to: "/development", label: "Leadership development", icon: Presentation },
    { to: "/capstone", label: "Capstone workspace", icon: GraduationCap },
    { to: "/resources", label: "Resource library", icon: FileText },
    { to: "/forum", label: "Discussion forum", icon: MessageCircle },
  ];
  const adminLinks = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/cohorts", label: "Cohorts", icon: ShieldCheck },
    { to: "/admin/participants", label: "Participants", icon: Users },
    { to: "/admin/modules", label: "Modules & resources", icon: BookOpen },
    { to: "/admin/records", label: "Attendance & completion", icon: FileText },
    { to: "/admin/registration", label: "Registration & credentials", icon: ClipboardCheck },
    { to: "/admin/program-tools", label: "Program tools", icon: CalendarDays },
    { to: "/admin/capstones", label: "Capstone reviews", icon: FileCheck2 },
    { to: "/admin/instructor", label: "Instructor workspace", icon: Presentation },
    { to: "/admin/analytics", label: "Program analytics", icon: BarChart3 },
    { to: "/admin/announcements", label: "Announcements", icon: Bell },
    { to: "/forum", label: "Discussion forum", icon: MessageCircle },
  ];
  const links = isAdmin ? adminLinks : participantLinks;
  return <div className={`app-shell ${mobileOpen ? "navigation-open" : ""}`}>
    <aside id="portal-navigation" className={`sidebar ${mobileOpen ? "open" : ""}`} aria-hidden={!mobileOpen ? undefined : false}>
      <div className="sidebar-brand"><span className="brand-mark">OL</span><span><strong>Officer to Leader</strong><small>Leadership development</small></span><button className="icon-button sidebar-close" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><X /></button></div>
      <nav aria-label="Primary navigation">{links.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} end={to === "/" || to === "/admin"} onClick={() => setMobileOpen(false)}><Icon size={19} /><span>{label}</span><ChevronRight className="nav-chevron" size={16} /></NavLink>)}</nav>
      <div className="sidebar-context"><p>{isAdmin ? "Administrative workspace" : "Your cohort"}</p><strong>{isAdmin ? "Program administration" : session.cohort?.name ?? "Assignment pending"}</strong>{!isAdmin && <span>{session.cohort?.agency_unit}</span>}</div>
      <NavLink className="sidebar-settings" to="/settings" onClick={() => setMobileOpen(false)}><Settings size={17} /><span>Account settings</span><ChevronRight size={15} /></NavLink>
    </aside>
    {mobileOpen && <button type="button" className="sidebar-backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
    <div className="main-shell">
      <header className="topbar"><button type="button" className="icon-button menu-button" onClick={() => setMobileOpen((open) => !open)} aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-controls="portal-navigation" aria-expanded={mobileOpen}><Menu /></button><div className="topbar-title">{isAdmin ? "Administration" : session.cohort?.name ?? "Officer to Leader"}</div><div className="user-summary"><span><strong>{session.user.full_name}</strong><small>{isAdmin ? "Administrator" : session.user.agency_section}</small></span><UserButton /></div></header>
      <main className="page-content">
        {isAdmin ? <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/cohorts" element={<AdminCohorts />} />
          <Route path="/admin/participants" element={<AdminParticipants />} />
          <Route path="/admin/modules" element={<AdminModules />} />
          <Route path="/admin/records" element={<AdminRecords />} />
          <Route path="/admin/registration" element={<AdminRegistrationManagement />} />
          <Route path="/admin/program-tools" element={<AdminProgramTools />} />
          <Route path="/admin/capstones" element={<AdminCapstones />} />
          <Route path="/admin/instructor" element={<AdminInstructorWorkspace />} />
          <Route path="/admin/check-ins" element={<AdminCheckIns />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/announcements" element={<AdminAnnouncements />} />
          <Route path="/forum" element={<ForumHome user={session.user} />} />
          <Route path="/forum/topics/:topicId" element={<ForumTopicPage user={session.user} />} />
          <Route path="/settings/*" element={<AccountSettings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes> : <Routes>
          <Route path="/" element={<ParticipantDashboard roadmapOnly={false} />} />
          <Route path="/roadmap" element={<ParticipantDashboard roadmapOnly />} />
          <Route path="/resources" element={<ParticipantResources />} />
          <Route path="/registration" element={<ParticipantRegistration />} />
          <Route path="/calendar" element={<ParticipantCalendar />} />
          <Route path="/reflections" element={<ParticipantReflections />} />
          <Route path="/development" element={<ParticipantDevelopment />} />
          <Route path="/capstone" element={<ParticipantCapstone />} />
          <Route path="/modules/:moduleId" element={<ParticipantModule />} />
          <Route path="/forum" element={<ForumHome user={session.user} />} />
          <Route path="/forum/topics/:topicId" element={<ForumTopicPage user={session.user} />} />
          <Route path="/settings/*" element={<AccountSettings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>}
      </main>
    </div>
  </div>;
}

function AccountSettings() {
  return <><header className="page-heading"><div><p className="eyebrow">Your account</p><h1>Account settings</h1><p>Update your profile, password, and sign-in security.</p></div></header><section className="account-settings"><UserProfile routing="path" path="/settings" /></section></>;
}

function AuthenticatedApp() {
  const api = useApi();
  const { isLoaded } = useUser();
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { if (isLoaded) api<Session>("/api/session").then(setSession).catch((err: Error) => setError(err.message)); }, [api, isLoaded]);
  if (error) return <div className="center-screen"><Notice>{error}</Notice><p>Sign out and contact your program administrator if the problem continues.</p><UserButton /></div>;
  if (!session) return <div className="center-screen"><Loading label="Preparing your portal" /></div>;
  return <AppLayout session={session} />;
}

export function App() {
  return <><Show when="signed-out"><AuthPages /></Show><Show when="signed-in"><AuthenticatedApp /></Show></>;
}
